import { ReactElement, useState } from 'react';
import {
  Datagrid,
  DateInput,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  required,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  Chip,
  Dialog,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { CustomPopover, usePopover } from 'src/components/custom-popover';
import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import { chains } from 'src/assets/data/chains';
import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import { RetryFailedDialog } from './RetryFailedDialog';
import { CancelFailedDialog } from './CancelFailedDialog';
import { MarkDoneDialog } from './MarkDoneDialog';
import { ExportButton } from './ExportButton';
import {
  DEFAULT_WITHDRAWALS_PROVIDER,
  FAILED_ACTION_ELIGIBLE_PROVIDERS,
  WITHDRAWAL_PROVIDERS,
  WITHDRAWAL_STATUSES,
  XBIT_RELAY_WITHDRAWALS_APPROVE_ACTION,
  XBIT_RELAY_WITHDRAWALS_CANCEL_FAILED_ACTION,
  XBIT_RELAY_WITHDRAWALS_MARK_DONE_ACTION,
  XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE,
  XBIT_RELAY_WITHDRAWALS_REJECT_ACTION,
  XBIT_RELAY_WITHDRAWALS_RETRY_FAILED_ACTION,
} from './constants';
import './init';

const truncate = (value: string, head = 8, tail = 6) =>
  value && value.length > head + tail + 3
    ? `${value.slice(0, head)}...${value.slice(-tail)}`
    : value || '-';

const explorerTxUrl = (chainId: number, txHash: string) => {
  if (!txHash) return '';
  const match = chains.find((c) => c.id === chainId);
  return match ? `${match.browser}${txHash}` : '';
};

const chainSymbol = (chainId: number | string) => {
  const id = Number(chainId);
  const match = chains.find((c) => c.id === id);
  return match ? match.symbol : String(chainId);
};

const statusColor = (status: string) => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
    case 'Rejected':
      return 'error';
    case 'PendingAdminApproval':
      return 'warning';
    case 'Pending':
    case 'Reviewing':
      return 'default';
    case 'Broadcasting':
    case 'Executing':
    case 'Confirming':
      return 'info';
    default:
      return 'default';
  }
};

type RowActionsMenuProps = {
  record: any;
  onPickAction: (dialog: ReactElement | undefined) => void;
};

const RowActionsMenu = ({ record, onPickAction }: RowActionsMenuProps) => {
  const t = useTranslate();
  const popover = usePopover();

  const isPendingApproval = record.status === 'PendingAdminApproval';
  const isFailed = record.status === 'Failed';
  const eligibleProvider = (
    FAILED_ACTION_ELIGIBLE_PROVIDERS as readonly string[]
  ).includes(record.provider);
  const showFailedActions = isFailed && eligibleProvider;

  if (!isPendingApproval && !showFailedActions) {
    return null;
  }

  const close = () => onPickAction(undefined);
  const open = (dialog: ReactElement) => {
    popover.onClose();
    onPickAction(dialog);
  };

  return (
    <>
      <IconButton
        size="small"
        color={popover.open ? 'inherit' : 'default'}
        onClick={popover.onOpen}
      >
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'right-top' } }}
      >
        <MenuList sx={{ minWidth: 180 }}>
          {isPendingApproval && (
            <PermissionGuard
              resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
              action={XBIT_RELAY_WITHDRAWALS_APPROVE_ACTION}
              fallback={<></>}
            >
              <MenuItem
                onClick={() =>
                  open(<ApproveDialog record={record} onClose={close} />)
                }
                sx={{ color: 'success.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:check-circle-bold" />
                </ListItemIcon>
                <ListItemText>{t('module.xbitRelayWithdrawals.approve')}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}

          {isPendingApproval && (
            <PermissionGuard
              resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
              action={XBIT_RELAY_WITHDRAWALS_REJECT_ACTION}
              fallback={<></>}
            >
              <MenuItem
                onClick={() =>
                  open(<RejectDialog record={record} onClose={close} />)
                }
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:close-circle-bold" />
                </ListItemIcon>
                <ListItemText>{t('module.xbitRelayWithdrawals.reject')}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}

          {showFailedActions && (
            <PermissionGuard
              resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
              action={XBIT_RELAY_WITHDRAWALS_RETRY_FAILED_ACTION}
              fallback={<></>}
            >
              <MenuItem
                onClick={() =>
                  open(<RetryFailedDialog record={record} onClose={close} />)
                }
                sx={{ color: 'warning.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:refresh-bold" />
                </ListItemIcon>
                <ListItemText>{t('module.xbitRelayWithdrawals.retry')}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}

          {showFailedActions && (
            <PermissionGuard
              resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
              action={XBIT_RELAY_WITHDRAWALS_CANCEL_FAILED_ACTION}
              fallback={<></>}
            >
              <MenuItem
                onClick={() =>
                  open(<CancelFailedDialog record={record} onClose={close} />)
                }
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:close-circle-bold" />
                </ListItemIcon>
                <ListItemText>{t('module.xbitRelayWithdrawals.cancelFailed')}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}

          {showFailedActions && (
            <PermissionGuard
              resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
              action={XBIT_RELAY_WITHDRAWALS_MARK_DONE_ACTION}
              fallback={<></>}
            >
              <MenuItem
                onClick={() =>
                  open(<MarkDoneDialog record={record} onClose={close} />)
                }
                sx={{ color: 'success.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <Iconify icon="solar:verified-check-bold" />
                </ListItemIcon>
                <ListItemText>{t('module.xbitRelayWithdrawals.markDone')}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}
        </MenuList>
      </CustomPopover>
    </>
  );
};

const WithdrawalsListActions = () => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      p: 2,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': {
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          width: '100%',
        },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelayWithdrawals.provider"
            choices={WITHDRAWAL_PROVIDERS.map((p) => ({ id: p, name: p }))}
            alwaysOn
            size="small"
            sx={{ minWidth: 200 }}
            validate={required()}
            isRequired
          />,
          <SelectInput
            key="status"
            source="status"
            label="module.xbitRelayWithdrawals.status"
            choices={WITHDRAWAL_STATUSES.map((s) => ({ id: s, name: s }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 200 }}
          />,
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelayWithdrawals.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
          <TextInput
            key="id"
            source="id"
            label="module.xbitRelayWithdrawals.recordId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
          <TextInput
            key="toAddress"
            source="toAddress"
            label="module.xbitRelayWithdrawals.toAddress"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="txHash"
            source="txHash"
            label="module.xbitRelayWithdrawals.txHash"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <DateInput
            key="timeFrom"
            source="timeFrom"
            label="common.fromTime"
            size="small"
            parse={(value: string) => (value ? dayjs(value).startOf('day').valueOf() : undefined)}
            format={(value: any) => (value ? dayjs(Number(value)).format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 180 }}
          />,
          <DateInput
            key="timeTo"
            source="timeTo"
            label="common.toTime"
            size="small"
            parse={(value: string) => (value ? dayjs(value).endOf('day').valueOf() : undefined)}
            format={(value: any) => (value ? dayjs(Number(value)).format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 180 }}
          />,
        ]}
      />
    </Box>
    <ExportButton />
  </TopToolbar>
);

const XbitRelayWithdrawalsList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_WITHDRAWALS_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelayWithdrawals.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelayWithdrawals.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={<WithdrawalsListActions />}
            empty={false}
            perPage={20}
            exporter={false}
            filterDefaultValues={{ provider: DEFAULT_WITHDRAWALS_PROVIDER }}
            pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
            sort={{ field: 'createdAtUnixMs', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelayWithdrawals.time"
                  sortable={false}
                  render={(record: any) =>
                    record.createdAtUnixMs ? (
                      <Tooltip
                        title={dayjs(Number(record.createdAtUnixMs)).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                        >
                          {dayjs(Number(record.createdAtUnixMs)).format(
                            'MM-DD HH:mm',
                          )}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.chainId"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={String(record.chainId)}>
                      <Chip
                        label={chainSymbol(record.chainId)}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.status"
                  sortable={false}
                  render={(record: any) => {
                    const errorText = record.errorMessage || record.errorMsg;
                    const chip = (
                      <Chip
                        label={record.status || '-'}
                        size="small"
                        color={statusColor(record.status) as any}
                        variant={
                          record.status === 'Pending' ? 'outlined' : 'filled'
                        }
                      />
                    );
                    return record.status === 'Failed' && errorText ? (
                      <Tooltip title={errorText}>{chip}</Tooltip>
                    ) : (
                      chip
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.errorMsg"
                  sortable={false}
                  render={(record: any) => {
                    const errorText = record.errorMessage || record.errorMsg;
                    if (!errorText) return '-';
                    const display = record.errorCode
                      ? `[${record.errorCode}] ${errorText}`
                      : errorText;
                    return (
                      <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-line' }}>
                        {display}
                      </Typography>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.token"
                  sortable={false}
                  render={(record: any) => {
                    const symbol = record.symbol || record.tokenSymbol;
                    if (symbol) {
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {symbol}
                        </Typography>
                      );
                    }
                    if (record.token) {
                      return (
                        <Tooltip title={record.token}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            onClick={() => copy(record.token)}
                          >
                            {truncate(record.token)}
                          </Typography>
                        </Tooltip>
                      );
                    }
                    return '-';
                  }}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.amount"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.amount || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.platformFee"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.platformFee ?? record.platform_fee ?? '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.toAddress"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.toAddress || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.toAddress)}
                      >
                        {truncate(record.toAddress)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.txHash"
                  sortable={false}
                  render={(record: any) => {
                    const txHash = record.txId || record.txHash;
                    if (!txHash) return '-';
                    const url = explorerTxUrl(Number(record.chainId), txHash);
                    if (!url) {
                      return (
                        <Tooltip title={txHash}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            onClick={() => copy(txHash)}
                          >
                            {truncate(txHash)}
                          </Typography>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={txHash}>
                        <Link
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {truncate(txHash)}
                        </Link>
                      </Tooltip>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayWithdrawals.userId"
                  sortable={false}
                  render={(record: any) =>
                    record.userId ? (
                      <Tooltip title={record.userId}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onClick={() => copy(record.userId)}
                        >
                          {truncate(record.userId)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label=""
                  sortable={false}
                  render={(record: any) => (
                    <RowActionsMenu record={record} onPickAction={setPopup} />
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog
          open={Boolean(popup)}
          onClose={() => setPopup(undefined)}
          maxWidth="sm"
          fullWidth
        >
          {popup}
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelayWithdrawalsList;
